import { useLogto } from "@logto/react";
import {
  streamAskHelena,
  streamAskHelenaCancel,
  streamAskHelenaConfirm,
} from "controller/api/helena/askHelena";
import { useCallback, useEffect, useRef } from "react";
import type { AskHelenaAbortReason, AskHelenaSseEvent } from "shared";

/** A tool-using answer runs 10-25s; 5 tool rounds on top of that is the ceiling. */
export const ASK_HELENA_STREAM_TIMEOUT_MS = 120000;

interface AskHelenaStreamInput {
  question: string;
  conversationId?: string;
  voiceSessionId?: string;
}

interface UseAskHelenaStreamReturn {
  ask: (input: AskHelenaStreamInput) => Promise<void>;
  /** Approves a proposed change and streams the rest of the turn. */
  confirm: (pendingWriteId: string) => Promise<void>;
  /** Declines it. Short stream, no model call. */
  cancel: (pendingWriteId: string) => Promise<void>;
  abort: (reason: AskHelenaAbortReason) => void;
}

/**
 * Transport only — opens the Ask Helena SSE stream and forwards every event to
 * `onEvent`. Knows nothing about Jotai or the chat UI; the feature hook
 * (`useAskHelena`) maps events onto state.
 *
 * Not a TanStack Query mutation on purpose: a stream's value is the sequence of
 * intermediate states, not its return value, and RQ's default `retry: 3` would
 * silently replay a 10-25s LLM call three times and bill the MSP for it. This
 * matches the other push-data hooks here (useTriageStatusMqtt et al).
 */
export const useAskHelenaStream = (
  onEvent: (event: AskHelenaSseEvent) => void,
  onStreamEnd?: (reason: AskHelenaAbortReason) => void,
): UseAskHelenaStreamReturn => {
  const { getAccessToken } = useLogto();
  const abortRef = useRef<AbortController | null>(null);
  const onEventRef = useRef(onEvent);
  const onStreamEndRef = useRef(onStreamEnd);

  // Kept in a ref so `ask` stays referentially stable without forcing every
  // caller to memoize its handler.
  useEffect(() => {
    onEventRef.current = onEvent;
    onStreamEndRef.current = onStreamEnd;
  }, [onEvent, onStreamEnd]);

  const abort = useCallback((reason: AskHelenaAbortReason) => {
    const controller = abortRef.current;
    if (!controller) return;

    abortRef.current = null;
    controller.abort();
    onStreamEndRef.current?.(reason);
  }, []);

  // StrictMode's double mount->unmount->mount runs this with a null ref, so it
  // is a no-op. That is only safe because `ask` is called from an event
  // handler, never an effect — an effect-started stream would run twice.
  useEffect(() => {
    return () => abort("teardown");
  }, [abort]);

  /**
   * Owns the lifecycle every stream shares: one in-flight controller, the
   * timeout, a fresh token, and the abort-is-not-an-error rule. `/ask`,
   * `/confirm` and `/cancel` differ only in which endpoint they open.
   */
  const runStream = useCallback(
    async (
      open: (input: {
        accessToken: string;
        signal: AbortSignal;
        onEvent: (event: AskHelenaSseEvent) => void;
      }) => Promise<void>,
    ) => {
      // Guards a rapid double-send: the previous turn is dropped, not queued.
      abort("superseded");

      const controller = new AbortController();
      abortRef.current = controller;
      const timeoutId = setTimeout(
        () => controller.abort(),
        ASK_HELENA_STREAM_TIMEOUT_MS,
      );

      try {
        // Must come from Logto at send time. `getAccessToken` in
        // utilz/helpers/CredentialData reads a Zustand snapshot seeded once at
        // bootstrap by SecureWrapper — it goes stale on a long session.
        const accessToken = await getAccessToken(
          import.meta.env.VITE_APP_BASE_URL,
        );
        if (!accessToken) throw new Error("Not authenticated");

        await open({
          accessToken,
          signal: controller.signal,
          onEvent: (event) => onEventRef.current(event),
        });
      } catch (error: unknown) {
        // Check the signal rather than error.name — "AbortError" is not
        // consistent across browsers and polyfills.
        if (controller.signal.aborted) return;
        throw error;
      } finally {
        clearTimeout(timeoutId);
        // Close the socket rather than letting it linger until the server
        // hangs up. Note: aborting does NOT stop server-side token spend.
        controller.abort();
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [abort, getAccessToken],
  );

  const ask = useCallback(
    ({ question, conversationId, voiceSessionId }: AskHelenaStreamInput) =>
      runStream((input) =>
        streamAskHelena({ question, conversationId, voiceSessionId, ...input }),
      ),
    [runStream],
  );

  const confirm = useCallback(
    (pendingWriteId: string) =>
      runStream((input) =>
        streamAskHelenaConfirm({ pendingWriteId, ...input }),
      ),
    [runStream],
  );

  const cancel = useCallback(
    (pendingWriteId: string) =>
      runStream((input) => streamAskHelenaCancel({ pendingWriteId, ...input })),
    [runStream],
  );

  return { ask, confirm, cancel, abort };
};
